import { useMemo, useState, type ReactElement } from "react";

import { formatInboundEmailThreadForDisplay } from "@integrator-src/api/document/document-inbound-email-parse";
import { type ControlledDocument, type DocumentInboundEmailThread } from "@/lib/documents";
import { hubPreviewFrameClassName } from "@/lib/hubLayoutClassNames";
import { cn } from "@/lib/utils";

import { DocumentFilePreview } from "./DocumentFilePreview";

export type DocumentDetailPreviewMode = "file" | "email";

const MAX_EMAIL_THREAD_CHARS = 400_000;

function previewToggleButtonClass(active: boolean): string {
  return cn(
    "rounded px-2 py-0.5 text-[11px] font-medium transition-colors",
    active ? "bg-white text-slate-900 shadow-sm" : "text-slate-600 hover:text-slate-900",
  );
}

function DocumentPreviewToggle(props: {
  mode: DocumentDetailPreviewMode;
  onModeChange: (mode: DocumentDetailPreviewMode) => void;
}): ReactElement {
  return (
    <div className="inline-flex rounded-md bg-slate-200/80 p-0.5" role="group" aria-label="Preview source">
      <button
        type="button"
        className={previewToggleButtonClass(props.mode === "file")}
        onClick={() => props.onModeChange("file")}
      >
        File
      </button>
      <button
        type="button"
        className={previewToggleButtonClass(props.mode === "email")}
        onClick={() => props.onModeChange("email")}
      >
        Email
      </button>
    </div>
  );
}

function DocumentInboundEmailThreadPreview(props: {
  thread: DocumentInboundEmailThread;
  fillAvailableHeight?: boolean;
  headerActions?: ReactElement;
}): ReactElement {
  const { text, truncated } = useMemo(() => {
    const formatted = formatInboundEmailThreadForDisplay(props.thread.thread_text);
    const truncatedText = formatted.length > MAX_EMAIL_THREAD_CHARS;
    return {
      text: truncatedText ? formatted.slice(0, MAX_EMAIL_THREAD_CHARS) : formatted,
      truncated: truncatedText,
    };
  }, [props.thread.thread_text]);

  const frameClass = props.fillAvailableHeight
    ? cn(hubPreviewFrameClassName("fill"), "overflow-auto")
    : cn(hubPreviewFrameClassName("inline"), "overflow-auto");

  return (
    <div
      className={cn(
        "rounded-lg border border-slate-200 bg-slate-50",
        props.fillAvailableHeight && "flex min-h-0 flex-1 flex-col",
      )}
    >
      <div className="flex shrink-0 items-center justify-between gap-2 border-b border-slate-200 px-3 py-2">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-slate-600">Inbound email</h3>
        {props.headerActions}
      </div>
      <div className={cn(props.fillAvailableHeight ? "flex min-h-0 flex-1 flex-col overflow-hidden" : undefined)}>
        <div className={cn("px-2 pb-2 pt-0", props.fillAvailableHeight && "min-h-0 flex-1")}>
          <div className={frameClass}>
            <pre className="whitespace-pre-wrap break-words px-3 py-2 font-mono text-xs leading-relaxed text-slate-800">
              {text}
            </pre>
            {truncated ? (
              <p className="border-t border-slate-200 px-3 py-2 text-xs text-slate-500">
                Showing the first {MAX_EMAIL_THREAD_CHARS.toLocaleString()} characters only.
              </p>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

export function documentInboundEmailThreadHoverHint(thread: DocumentInboundEmailThread): string {
  const formatted = formatInboundEmailThreadForDisplay(thread.thread_text);
  const max = 700;
  if (formatted.length <= max) return formatted;
  return `${formatted.slice(0, max)}…`;
}

export function isInboundEmailCreatedEvent(eventNote: string | null | undefined): boolean {
  return (eventNote ?? "").toLowerCase().includes("inbound email");
}

export function DocumentDetailPreviewPane(props: {
  document: ControlledDocument;
  inboundEmailThread: DocumentInboundEmailThread | null | undefined;
  fillAvailableHeight?: boolean;
  previewMode?: DocumentDetailPreviewMode;
  onPreviewModeChange?: (mode: DocumentDetailPreviewMode) => void;
}): ReactElement {
  const [internalMode, setInternalMode] = useState<DocumentDetailPreviewMode>("file");
  const hasEmailThread = props.inboundEmailThread != null && props.inboundEmailThread.thread_text.trim() !== "";
  const mode = props.previewMode ?? internalMode;
  const setMode = props.onPreviewModeChange ?? setInternalMode;

  const toggle = hasEmailThread ? <DocumentPreviewToggle mode={mode} onModeChange={setMode} /> : undefined;

  if (hasEmailThread && mode === "email" && props.inboundEmailThread) {
    return (
      <DocumentInboundEmailThreadPreview
        thread={props.inboundEmailThread}
        fillAvailableHeight={props.fillAvailableHeight}
        headerActions={toggle}
      />
    );
  }

  return (
    <DocumentFilePreview
      document={props.document}
      fillAvailableHeight={props.fillAvailableHeight}
      headerActions={toggle}
    />
  );
}
